import { type ExtractValues } from '@craft/lib';

import { getPlatform, Platform } from './platform';

export enum KeyboardKeyId {
  Alt = 'Alt',
  Command = 'Command',
  Control = 'Control',
  Ctrl = 'Ctrl',
  Option = 'Option',
}

type PlatformMap<T> = Partial<Record<Platform, T>>;

type BaseKeyboardKey = {
  readonly abbreviatedLabel?: PlatformMap<null | string> | string;
  readonly id: KeyboardKeyId;
  readonly label: PlatformMap<null | string> | string;
  readonly supportedPlatforms: Platform[];
  readonly symbol: null | PlatformMap<null | string> | string;
};

const KeyboardKeys = [
  {
    abbreviatedLabel: 'Cmd',
    id: KeyboardKeyId.Command,
    label: 'Command',
    supportedPlatforms: [Platform.Mac],
    symbol: '⌘',
  },
  {
    abbreviatedLabel: 'Alt',
    id: KeyboardKeyId.Alt,
    label: 'Alt',
    supportedPlatforms: [Platform.Windows, Platform.Android],
    symbol: null,
  },
  {
    abbreviatedLabel: 'Opt',
    id: KeyboardKeyId.Option,
    label: 'Option',
    supportedPlatforms: [Platform.Mac],
    symbol: '⌥',
  },
  /* Do not use this value unless you actually need a Mac `Control` key. This `Control` key does not
  like a typical modifier key, so it is unlikely to be what you are looking for. */
  {
    abbreviatedLabel: 'Ctrl',
    id: KeyboardKeyId.Control,
    label: 'Control',
    supportedPlatforms: [Platform.Mac],
    symbol: '⌃',
  },
  // In most cases, this is the Ctrl key you are looking for.
  {
    abbreviatedLabel: 'Ctrl',
    id: KeyboardKeyId.Ctrl,
    label: 'Ctrl',
    supportedPlatforms: [Platform.Windows, Platform.Android, Platform.Other],
    symbol: null,
  },
] as const satisfies BaseKeyboardKey[];

export type KeyboardKey<I extends KeyboardKeyId = KeyboardKeyId> = Extract<
  (typeof KeyboardKeys)[number],
  { id: I }
>;

export const getKeyboardKey = <I extends KeyboardKeyId>(id: I): KeyboardKey<I> => {
  const key = KeyboardKeys.find(keyboardKey => keyboardKey.id === id);
  if (!key) {
    throw new Error(`The provided keyboard key ID '${id}' is invalid!`);
  }
  return key as KeyboardKey<I>;
};

type ParseMultiPlatformValue<V extends null | PlatformMap<null | string> | string> =
  V extends string ? V : V extends null ? null : ExtractValues<V>;

type KeyboardSymbol<I extends KeyboardKeyId = KeyboardKeyId> = null | ParseMultiPlatformValue<
  KeyboardKey<I>['symbol']
>;

export const getKeyboardSymbol = <I extends KeyboardKeyId>(id: I): KeyboardSymbol<I> => {
  const model = getKeyboardKey(id);
  const platform = getPlatform();
  if (typeof model.symbol === 'string') {
    return model.symbol as KeyboardSymbol<I>;
  }
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
  return model.symbol?.[platform] ?? null;
};

type KeyboardLabel<I extends KeyboardKeyId = KeyboardKeyId> = null | ParseMultiPlatformValue<
  KeyboardKey<I>['label']
>;

export const getKeyboardLabel = <I extends KeyboardKeyId>(id: I): KeyboardLabel<I> => {
  const model = getKeyboardKey(id) as BaseKeyboardKey;
  const platform = getPlatform();

  if (typeof model.label === 'string') {
    return model.label as KeyboardLabel<I>;
  }
  return (model.label[platform] ?? null) as KeyboardLabel<I>;
};

type KeyboardAbbvLabel<I extends KeyboardKeyId = KeyboardKeyId> = ParseMultiPlatformValue<
  KeyboardKey<I>['abbreviatedLabel']
>;

export const getKeyboardAbbvLabel = <I extends KeyboardKeyId>(id: I): KeyboardAbbvLabel<I> => {
  const model = getKeyboardKey(id) as BaseKeyboardKey;
  const platform = getPlatform();

  if (typeof model.abbreviatedLabel === 'string') {
    return model.abbreviatedLabel as KeyboardAbbvLabel<I>;
  }
  return (model.abbreviatedLabel?.[platform] ?? null) as KeyboardAbbvLabel<I>;
};

/**
 * Returns the first supported keyboard key, {@link KeyboardKey}, associated with any of the
 * provided {@link KeyboardKeyId} values, based on the current user's platform, {@link Platform}.
 *
 * @param {I[]} ids
 *   An array of {@link KeyboardKeyId} values that should be checked for support.  If any are
 *   supported by the current user's platform, then the first supported {@link KeyboardKey} will be
 *   returned.  Otherwise, null will be returned.
 *
 * @returns {KeyboardKey<I> | null}
 *   Either the first supported keyboard key {@link KeyboardKey} or null if no keyboard keys are
 *   supported by the current user's platform.
 */
export const getSupportedKeyboardKey = <I extends KeyboardKeyId>(
  ids: I[],
): KeyboardKey<I> | null => {
  const platform = getPlatform();
  for (const id of ids) {
    const key = getKeyboardKey(id);
    if ([...key.supportedPlatforms].includes(platform)) {
      return key;
    }
  }
  return null;
};

export enum KeyboardNavigationKey {
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp',
  End = 'End',
  Home = 'Home',
}

const isKeyboardNavigationKeyKey = (key: string): key is keyof typeof KeyboardNavigationKey =>
  Object.keys(KeyboardNavigationKey).includes(key);

export const getKeyboardNavigationKey = (key: string): KeyboardNavigationKey | null => {
  if (isKeyboardNavigationKeyKey(key)) {
    return KeyboardNavigationKey[key];
  }
  return null;
};
